import { Flame, Clock, Activity, Zap, ShieldAlert, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { useUICollapse } from '../hooks/useUICollapse';
import { Button } from '../../../ui/common-ui/button';

export type ViewFilterType = 'most-risky' | 'recent-alerts' | 'most-active' | 'recently-active' | 'critical';

interface ViewFilterProps {
  selected: ViewFilterType;
  onChange: (filter: ViewFilterType) => void;
}

const filters: { id: ViewFilterType; label: string; icon: React.ElementType; description: string }[] = [
  { id: 'most-risky', label: 'Most Risky', icon: Flame, description: 'Most alerts attached' },
  { id: 'recent-alerts', label: 'Recent Alerts', icon: Clock, description: 'Most recent alert date' },
  { id: 'most-active', label: 'Most Active', icon: Activity, description: 'Highest activity level' },
  { id: 'recently-active', label: 'Recently Active', icon: Zap, description: 'Most recent activity date' },
  { id: 'critical', label: 'Critical Entities', icon: ShieldAlert, description: 'Critical alerts attached' },
];

export function CollapsibleViewFilter({ selected, onChange }: ViewFilterProps) {
  const { isCollapsed, setHoveredElement, hoveredElement } = useUICollapse();
  const isHovered = hoveredElement === 'viewfilter';
  const showExpanded = !isCollapsed || isHovered;

  // Get current selected filter
  const selectedFilter = filters.find(f => f.id === selected);
  const SelectedIcon = selectedFilter?.icon || Flame;

  return (
    <div
      className={cn(
        "bg-card border border-border/50 shadow-lg overflow-hidden transition-[width,border-radius] duration-300 ease-out flex-shrink-0",
        showExpanded ? "w-64 rounded-2xl" : "w-14 rounded-xl"
      )}
      onMouseEnter={() => setHoveredElement('viewfilter')}
      onMouseLeave={() => setHoveredElement(null)}
    >
      {/* Trigger Button - Always visible */}
      <Button
        variant="ghost"
        className={cn(
          "w-full h-14 px-2 overflow-hidden hover:bg-muted/50 transition-colors relative !justify-start",
          showExpanded ? "rounded-t-2xl rounded-b-none" : "rounded-xl"
        )}
      >
        {/* Icon is always anchored to the left */}
        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <SelectedIcon className="w-5 h-5 text-primary" />
        </div>

        {/* Text is absolutely positioned so icon never shifts */}
        <div
          className={cn(
            "absolute left-14 right-8 top-1/2 -translate-y-1/2 overflow-hidden transition-opacity duration-200 ease-out flex items-center",
            showExpanded ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        >
          <span className="text-sm font-semibold text-foreground truncate block text-left">
            {selectedFilter?.label || 'Select Filter'}
          </span>
        </div>

        <ChevronRight
          className={cn(
            "absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground transition-all duration-200",
            showExpanded ? "opacity-100 rotate-90" : "opacity-0"
          )}
        />
      </Button>

      {/* Filter Options - Only visible when expanded */}
      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-out",
        showExpanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
      )}>
        <div className="p-2 pt-0 space-y-1">
          {filters.map(({ id, label, icon: Icon, description }) => (
            <button
              key={id}
              onClick={() => onChange(id)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left group",
                selected === id ? "bg-primary/10" : "hover:bg-muted/50"
              )}
            >
              <div className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center transition-colors flex-shrink-0",
                selected === id
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground group-hover:bg-muted-foreground/20"
              )}>
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className={cn(
                  "text-sm font-medium truncate text-left",
                  selected === id ? "text-primary" : "text-foreground"
                )}>
                  {label}
                </p>
                <p className="text-xs text-muted-foreground truncate text-left">
                  {description}
                </p>
              </div>
              {selected === id && (
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
